/**
 * Cirurgias a Faturar Service - Surgery Billing System
 *
 * Endpoints:
 * - POST /api/jobs/{job_id}/cirurgias/upload - Upload Excel
 * - GET /api/jobs/{job_id}/cirurgias/items - List items
 * - POST /api/jobs/{job_id}/cirurgias/items - Add item
 * - PUT /api/jobs/{job_id}/cirurgias/items/{item_id} - Update item
 * - DELETE /api/jobs/{job_id}/cirurgias/items/{item_id} - Delete item
 * - GET /api/jobs/{job_id}/cirurgias/download - Download Excel
 */
import apiClient from './apiClient';

// ============================================================
// TYPES
// ============================================================

export interface ItemCirurgiaFaturar {
  id: number;
  data_hora: string; // ISO datetime
  paciente: string;
  medico: string;
  convenio: string;
  codigo_produto: string;
  descricao_produto: string;
  lote: string;
  quantidade: string; // Decimal as string
  cnpj: string;
  com_regime: boolean;
  regime_label: string; // "Com Regime" | "Sem Regime"
  cliente_id: number | null;
  cliente_nome_fantasia: string;
  status_processamento: string; // "PROCESSADO" | "ERRO" | "MANUAL"
  created_at: string;
}

export interface CirurgiaFaturarDados {
  id: number;
  job_id: number;
  arquivo_nome: string;
  total_linhas: number;
  total_processadas: number;
  total_erros: number;
  taxa_sucesso: number;
  status_importacao: string;
  data_importacao: string;
  erros: string[];
  itens_count: number;
}

export interface CirurgiaItemsResponse {
  job_id: number;
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  itens: ItemCirurgiaFaturar[];
}

export interface ItemCirurgiaFaturarInput {
  data_hora: string; // ISO datetime
  paciente: string;
  medico: string;
  convenio: string;
  codigo_produto: string;
  lote: string;
  quantidade: number;
  cnpj: string;
  cliente_id?: number;
  com_regime?: boolean;
  descricao_produto?: string;
}

export interface ItemCirurgiaFaturarUpdate {
  quantidade?: number;
  data_hora?: string;
  paciente?: string;
  medico?: string;
  convenio?: string;
  codigo_produto?: string;
  lote?: string;
  cnpj?: string;
  com_regime?: boolean;
}

export interface AddCirurgiaResponse {
  success: boolean;
  message: string;
  item: ItemCirurgiaFaturar;
}

export interface UpdateCirurgiaResponse {
  success: boolean;
  message: string;
  item: ItemCirurgiaFaturar;
}

export interface DeleteCirurgiaResponse {
  success: boolean;
  message: string;
}

// ============================================================
// API CLIENT
// ============================================================

class CirurgiasFaturarService {
  /**
   * Upload Excel file with surgeries
   * POST /api/jobs/{job_id}/cirurgias/upload
   */
  async uploadExcel(
    jobId: number,
    file: File,
    clienteId?: number,
    comRegime?: boolean
  ): Promise<CirurgiaFaturarDados> {
    const formData = new FormData();
    formData.append('file', file);
    if (clienteId) {
      formData.append('cliente_id', clienteId.toString());
    }
    if (comRegime !== undefined) {
      formData.append('com_regime', comRegime.toString());
    }

    const response = await apiClient.post(`/jobs/${jobId}/cirurgias/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Get surgery items for a job (paginated)
   * GET /api/jobs/{job_id}/cirurgias/items
   */
  async getItems(
    jobId: number,
    page: number = 1,
    pageSize: number = 100
  ): Promise<CirurgiaItemsResponse> {
    const response = await apiClient.get(`/jobs/${jobId}/cirurgias/items`, {
      params: { page, page_size: pageSize },
    });

    return response.data;
  }

  /**
   * Get all surgery items (all pages)
   */
  async getAllItems(jobId: number): Promise<ItemCirurgiaFaturar[]> {
    const items: ItemCirurgiaFaturar[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await this.getItems(jobId, page, 100);
      items.push(...response.itens);

      if (response.itens.length < 100) {
        hasMore = false;
      } else {
        page++;
      }
    }

    return items;
  }

  /**
   * Add a manual surgery item
   * POST /api/jobs/{job_id}/cirurgias/items
   */
  async addItem(jobId: number, data: ItemCirurgiaFaturarInput): Promise<AddCirurgiaResponse> {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/cirurgias/items`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao adicionar cirurgia');
    }
  }

  /**
   * Update a surgery item (mainly quantity)
   * PUT /api/jobs/{job_id}/cirurgias/items/{item_id}
   */
  async updateItem(
    jobId: number,
    itemId: number,
    data: ItemCirurgiaFaturarUpdate
  ): Promise<UpdateCirurgiaResponse> {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/cirurgias/items/${itemId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao atualizar cirurgia');
    }
  }

  /**
   * Delete a surgery item
   * DELETE /api/jobs/{job_id}/cirurgias/items/{item_id}
   */
  async deleteItem(jobId: number, itemId: number): Promise<DeleteCirurgiaResponse> {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/cirurgias/items/${itemId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao excluir cirurgia');
    }
  }

  /**
   * Download Excel file
   * GET /api/jobs/{job_id}/cirurgias/download
   */
  async downloadExcel(jobId: number): Promise<Blob> {
    const response = await apiClient.get(`/jobs/${jobId}/cirurgias/download`, {
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Helper: Download Excel and trigger browser download
   */
  async downloadAndSaveExcel(jobId: number, filename?: string): Promise<void> {
    try {
      const blob = await this.downloadExcel(jobId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `cirurgias_faturar_${jobId}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao fazer download do Excel');
    }
  }

  /**
   * Format date for display (DD/MM/YYYY HH:MM)
   */
  formatDataHora(isoDate: string): string {
    if (!isoDate) return '-';
    const date = new Date(isoDate);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Convert ItemCirurgiaFaturar to UI table format
   */
  convertToUIFormat(item: ItemCirurgiaFaturar) {
    return {
      id: item.id,
      dataHora: this.formatDataHora(item.data_hora),
      paciente: item.paciente,
      medico: item.medico,
      convenio: item.convenio,
      produto: item.codigo_produto,
      descricao: item.descricao_produto,
      lote: item.lote,
      quantidade: parseFloat(item.quantidade),
      cnpj: item.cnpj,
      cliente: item.cliente_nome_fantasia || '-',
      regime: item.regime_label || '-',
      status: item.status_processamento,
    };
  }
}

export default new CirurgiasFaturarService();
